import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { SectionBlock } from 'src/registry/components/blocks/SectionBlock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { MembershipType } from 'src/features/membershipType/types';
import MembershipZoneRulesTable from 'src/features/membership/components/table/MembershipZoneRulesTable';
import IdentifiersTable from 'src/features/membership/components/table/IdentifiersTable';
import PaymentsTable from 'src/features/payment/components/tables/PaymentsTable';
import GatewayPassesTable from 'src/features/gatewayPass/components/GatewayPassesTable';
import PastStaysTable from 'src/features/stay/components/PastStaysTable';
import RestrictedGatewaysTable from 'src/features/membershipType/components/table/RestrictedGatewaysTable';
import AllowedTimesTable from 'src/features/membershipType/components/table/AllowedTimesTable';
import MembershipAllowedTimesTable from 'src/features/membership/components/table/MembershipAllowedTimesTable';
import ZoneRulesTable from 'src/features/membershipType/components/table/ZoneRulesTable';
import MembershipCloudUsersTable from 'src/features/MembershipCloudUsers/components/tables/MembershipCloudUsersTable';
import { membershipUsersMessages } from 'src/features/MembershipCloudUsers/utils/messages';
import { PAYMENT_ITEM_TYPE } from 'src/features/payment/utils/types';
import { userMessages } from 'src/features/users/utils/messages';
import { MembershipInvitationTable } from 'src/features/MembershipCloudUsers/components/MembershipInvitationTable';

type MembershipDashboardTablesProps = {
    membershipTypeData?: MembershipType;
    templateId: string;
};

const MembershipDashboardTables = ({ membershipTypeData, templateId }: MembershipDashboardTablesProps) => {
    const { membershipId = '' } = useParams();
    const accessMembershipsPermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessPaymentsPermission = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const accessGatewayPassesPermission = useHasPermissions([PERMISSION.ACCESS_GATEWAY_PASSES]);
    const accessPastStaysPermission = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessMembershipTypePermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);
    const accessMembershipUsers = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);

    const accessConfigurationTables = accessMembershipsPermission || accessMembershipTypePermission;

    const defaultTabValue = (() => {
        if (accessMembershipsPermission) return 'identifiers';
        if (accessMembershipUsers) return 'membership-users';
        if (accessMembershipUsers) return 'membership-users-invitations';
        if (accessConfigurationTables) return 'configuration';
        if (accessPastStaysPermission) return 'past-stays';
        if (accessGatewayPassesPermission) return 'gateway-passes';
        if (accessPaymentsPermission) return 'payments';
    })();

    return (
        <div className="grid flex-3 grid-cols-1 pr-8">
            <Tabs defaultValue={defaultTabValue}>
                <TabsList>
                    {accessMembershipsPermission && (
                        <TabsTrigger value="identifiers">
                            <FormattedMessage {...membershipMessages.identifiers} />
                        </TabsTrigger>
                    )}
                    {accessMembershipUsers && (
                        <TabsTrigger value="membership-users">
                            <FormattedMessage {...membershipUsersMessages.membershipUsers} />
                        </TabsTrigger>
                    )}
                    {accessMembershipUsers && (
                        <TabsTrigger value="membership-users-invitations">
                            <FormattedMessage {...userMessages.invitations} />
                        </TabsTrigger>
                    )}
                    {accessConfigurationTables && (
                        <TabsTrigger value="configuration">
                            <FormattedMessage {...membershipMessages.configuration} />
                        </TabsTrigger>
                    )}
                    {accessPastStaysPermission && (
                        <TabsTrigger value="past-stays">
                            <FormattedMessage {...membershipMessages.pastStays} />
                        </TabsTrigger>
                    )}
                    {accessGatewayPassesPermission && (
                        <TabsTrigger value="gateway-passes">
                            <FormattedMessage {...membershipMessages.gatewayPasses} />
                        </TabsTrigger>
                    )}
                    {accessPaymentsPermission && (
                        <TabsTrigger value="payments">
                            <FormattedMessage {...membershipMessages.payments} />
                        </TabsTrigger>
                    )}
                </TabsList>
                {accessMembershipsPermission && (
                    <TabsContent value="identifiers" className="w-full">
                        <IdentifiersTable membershipId={membershipId} />
                    </TabsContent>
                )}
                {accessMembershipUsers && (
                    <TabsContent value="membership-users" className="w-full">
                        <MembershipCloudUsersTable membershipId={membershipId} />
                    </TabsContent>
                )}
                {accessMembershipUsers && (
                    <TabsContent value="membership-users-invitations">
                        <MembershipInvitationTable membershipId={membershipId} />
                    </TabsContent>
                )}
                {accessConfigurationTables && (
                    <TabsContent value="configuration" className="w-full">
                        <SectionBlock>
                            {accessMembershipsPermission && (
                                <>
                                    {membershipTypeData?.allow_custom_space_count_definition ? (
                                        <MembershipZoneRulesTable membershipId={membershipId} />
                                    ) : (
                                        <ZoneRulesTable inherited membershipTypeId={templateId} />
                                    )}
                                    {membershipTypeData?.has_time_restriction &&
                                    !membershipTypeData?.membership_type_allowed_times.length ? (
                                        <MembershipAllowedTimesTable membershipId={membershipId} />
                                    ) : (
                                        <AllowedTimesTable inherited membershipTypeId={templateId} />
                                    )}
                                </>
                            )}

                            {accessMembershipTypePermission && (
                                <RestrictedGatewaysTable inherited membershipTypeId={templateId} />
                            )}
                        </SectionBlock>
                    </TabsContent>
                )}
                {accessPastStaysPermission && (
                    <TabsContent value="past-stays" className="w-full">
                        <PastStaysTable membership={membershipId} />
                    </TabsContent>
                )}
                {accessGatewayPassesPermission && (
                    <TabsContent value="gateway-passes" className="w-full">
                        <GatewayPassesTable membership={membershipId} />
                    </TabsContent>
                )}
                {accessPaymentsPermission && (
                    <TabsContent value="payments" className="w-full">
                        <PaymentsTable entityId={membershipId} itemType={PAYMENT_ITEM_TYPE.MEMBERSHIP} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
};

export default MembershipDashboardTables;
